import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { products, categoryHierarchy } from '../mockData';
import { Search, ChevronRight, Filter } from 'lucide-react';
import ScrollReveal from '../components/ScrollReveal';

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');

  const activeCategory = searchParams.get('category') || 'All';
  const activeSubcategory = searchParams.get('subcategory') || 'All';

  const handleCategoryClick = (name) => {
    if (name === 'All') {
      setSearchParams({});
    } else {
      setSearchParams({ category: name });
    }
  };

  const handleSubcategoryClick = (name) => {
    if (name === 'All') {
      setSearchParams({ category: activeCategory });
    } else {
      setSearchParams({
        category: activeCategory,
        subcategory: name,
      });
    }
  };

  const filteredProducts = products.filter((p) => {
    const matchesCategory =
      activeCategory === 'All' || p.category === activeCategory;

    const matchesSubcategory =
      activeSubcategory === 'All' ||
      p.subcategory === activeSubcategory;

    const matchesSearch = p.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    return (
      matchesCategory &&
      matchesSubcategory &&
      matchesSearch
    );
  });

  const parentCategories = [
    'All',
    ...Object.keys(categoryHierarchy),
  ];

  const availableSubCategories =
    activeCategory !== 'All'
      ? ['All', ...categoryHierarchy[activeCategory]]
      : [];

  return (
    <div className="min-h-screen pt-20 bg-[#FBFBFB]">
      <section className="bg-white border-b border-gray-100 py-16">
        <div className="max-w-7xl mx-auto px-6 text-left">
          <h1 className="text-4xl font-bold text-[#1a1a1a] mb-2 tracking-tight">
            Our Collection
          </h1>

          <p className="text-gray-400">
            Precision-crafted furniture for{' '}
            {activeCategory === 'All'
              ? 'every workspace'
              : activeCategory.toLowerCase() + ' environments'}
            .
          </p>
        </div>
      </section>

      <section className="sticky top-20 z-40 bg-white/90 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-2 lg:pb-0">
              <Filter
                size={16}
                className="text-gray-400 mr-2 shrink-0"
              />

              {parentCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => handleCategoryClick(cat)}
                  className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all shrink-0 ${
                    activeCategory === cat
                      ? 'bg-black text-white shadow-lg'
                      : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="relative w-full lg:max-w-xs">
              <Search
                size={14}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              />

              <input
                type="text"
                placeholder="Search inventory..."
                value={searchQuery}
                onChange={(e) =>
                  setSearchQuery(e.target.value)
                }
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border-none rounded-xl text-sm focus:ring-1 focus:ring-black transition-all"
              />
            </div>
          </div>

          {activeCategory !== 'All' &&
            availableSubCategories.length > 1 && (
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-50 overflow-x-auto no-scrollbar pb-2">
                {availableSubCategories.map((sub) => (
                  <button
                    key={sub}
                    onClick={() =>
                      handleSubcategoryClick(sub)
                    }
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all shrink-0 ${
                      activeSubcategory === sub
                        ? 'bg-[#C1A57B] text-white'
                        : 'text-gray-400 border border-transparent hover:border-gray-200 hover:text-[#C1A57B]'
                    }`}
                  >
                    {sub}
                  </button>
                ))}
              </div>
            )}
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center mb-10 text-[11px] font-bold uppercase tracking-widest text-gray-300">
            <div className="flex items-center gap-2">
              <span className="text-gray-400">
                Products
              </span>

              <ChevronRight size={10} />

              <span
                className={
                  activeCategory !== 'All'
                    ? 'text-black'
                    : ''
                }
              >
                {activeCategory}
              </span>

              {activeSubcategory !== 'All' && (
                <>
                  <ChevronRight size={10} />
                  <span className="text-[#C1A57B]">
                    {activeSubcategory}
                  </span>
                </>
              )}
            </div>

            <p className="text-black">
              {filteredProducts.length} Items
            </p>
          </div>

          {filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-12">
              {filteredProducts.map((product, index) => (
                <ScrollReveal
                  key={product.id}
                  delay={index * 20}
                >
                  <Link
                    to={`/product/${product.id}`}
                    className="group block text-left"
                  >
                    {/* UPDATED IMAGE SECTION */}
                    <div className="aspect-[4/5] bg-[#f3f3f3] rounded-2xl overflow-hidden mb-6 relative flex items-center justify-center">
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-500 z-10" />

                      <img
                        src={
                          product.images &&
                          product.images.length > 0
                            ? product.images[0]
                            : product.image
                        }
                        alt={product.name}
                        className="max-w-[95%] max-h-[95%] object-contain group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>

                    <div className="px-2">
                      <p className="text-[9px] font-black uppercase tracking-[0.2em] text-[#C1A57B] mb-2">
                        {product.subcategory}
                      </p>

                      <h3 className="text-sm font-bold text-[#1a1a1a] mb-1 group-hover:text-[#C1A57B] transition-colors">
                        {product.name}
                      </h3>

                      <p className="text-xs text-gray-400 font-medium mb-3 line-clamp-1">
                        {product.description}
                      </p>

                      <p className="text-xs font-bold text-gray-400 tracking-wide uppercase">
                        Price on Request
                      </p>
                    </div>
                  </Link>
                </ScrollReveal>
              ))}
            </div>
          ) : (
            <div className="text-center py-40 border-2 border-dashed border-gray-100 rounded-3xl">
              <p className="text-gray-300 font-medium italic">
                No matches found for your selection.
              </p>

              <button
                onClick={() =>
                  handleCategoryClick('All')
                }
                className="mt-4 text-xs font-bold uppercase tracking-widest text-black underline"
              >
                Show All
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Products;
